import React, { useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";

import { Icon } from "../../../components/ui";

/**
 * Auto-save for the quote's Formik forms (house rule: no Save buttons). Rendered
 * INSIDE a <Formik>; every settled change submits the form through its own
 * onSubmit, so the payload logic stays exactly where it already lives.
 *
 * Only the quote workspace (stores on the new, no-pop-up setup) mounts it. The
 * DAO setup keeps its Save buttons, untouched.
 *
 * - Debounced: one PUT per pause, not one per keystroke.
 * - Flushes on unmount: closing the quote straight after typing still saves.
 * - The initial values never trigger a save.
 */
export default function FormikAutoSave({ debounce = 800 }) {
  const { values, submitForm, validateForm, setTouched } = useFormikContext();
  const [status, setStatus] = useState("idle");
  const saved = useRef(JSON.stringify(values));
  const timer = useRef(null);
  const pending = useRef(null);
  const submitRef = useRef(submitForm);
  submitRef.current = submitForm;
  const validateRef = useRef(validateForm);
  validateRef.current = validateForm;

  const run = async (snapshot) => {
    pending.current = null;
    // Formik skips onSubmit on a validation error and still resolves — this
    // used to say "Saved" for an edit that never left the browser.
    const errors = await validateRef.current();
    if (Object.keys(errors || {}).length) {
      // Show the message on the field itself, not only under the form.
      setTouched(Object.fromEntries(Object.keys(errors).map((k) => [k, true])), false);
      setStatus("invalid");
      return;
    }
    saved.current = snapshot;
    setStatus("saving");
    try {
      await submitRef.current();
      setStatus("saved");
    } catch (e) {
      setStatus("error");
    }
  };

  useEffect(() => {
    const snapshot = JSON.stringify(values);
    if (snapshot === saved.current) return undefined;
    pending.current = snapshot;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run(snapshot), debounce);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  // Leaving the quote mid-pause must not drop the edit.
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      if (pending.current) submitRef.current();
    },
    []
  );

  if (status === "idle") return null;

  return (
    <div className={`qws-autosave qws-autosave--${status}`} role="status" aria-live="polite">
      {status === "saving" && "Saving…"}
      {status === "saved" && (
        <>
          <Icon name="Check" size="sm" /> Saved
        </>
      )}
      {status === "error" && "Not saved — edit again to retry"}
      {status === "invalid" && "Not saved — fix the highlighted field"}
    </div>
  );
}
